import { View, Text, Pressable } from "react-native";
import Svg, { Circle } from "react-native-svg";
import { ClipboardList, ChevronRight, Check } from "lucide-react-native";
import { EMBER } from "../../../../onboarding/theme";
import { SectionTitle, MoonDivider, cardSurface } from "@louise/moon-ui";
import { useTranslations } from "@src/packages/i18n";
import { usePatient } from "@src/packages/patients/hooks/usePatient";
import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";
import {
  getMedicalHistoryProgressSummary,
  getVisibleMedicalHistoryGroups,
} from "@src/packages/medical-history/sectionRegistry";

type SectionGroup = {
  key: string;
  labelKey: string;
  completed: number;
  total: number;
};

type Props = {
  onPress?: () => void;
};

export function useCompletion() {
  const { data: patient, isLoading } = usePatient();
  const summary = getMedicalHistoryProgressSummary(patient);
  const groups = getVisibleMedicalHistoryGroups(patient).map((group) => {
    const completed = group.sections.filter(
      (section) => summary.completionData[section.key].percent >= 0.999,
    ).length;
    return {
      key: group.key,
      labelKey: group.titleKey,
      completed,
      total: group.sections.length,
    };
  });

  if (isLoading || !patient) {
    return {
      isLoading,
      progressPercent: 0,
      completedCategories: 0,
      totalCategories: summary.totalSections,
      groups: [] as SectionGroup[],
    };
  }

  const completedCategories = summary.completedSections;
  return {
    isLoading: false,
    progressPercent: summary.progressPercent,
    completedCategories,
    totalCategories: summary.totalSections,
    groups,
  };
}

// Mini progress ring component
function ProgressRing({
  percent,
  size = 56,
  strokeWidth = 5,
}: {
  percent: number;
  size?: number;
  strokeWidth?: number;
}) {
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference * (1 - percent / 100);

  return (
    <View style={{ width: size, height: size }}>
      <Svg width={size} height={size}>
        {/* Background track */}
        <Circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke="rgba(255,255,255,0.2)"
          strokeWidth={strokeWidth}
          fill="none"
        />
        {/* Progress arc */}
        <Circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke={percent >= 100 ? "#4ADE80" : EMBER.colors.warm}
          strokeWidth={strokeWidth}
          fill="none"
          strokeDasharray={`${circumference}`}
          strokeDashoffset={strokeDashoffset}
          strokeLinecap="round"
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
        />
      </Svg>
      <View
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 13,
            fontWeight: "700",
            color: percent >= 100 ? "#4ADE80" : EMBER.colors.text,
          }}
        >
          {percent}%
        </Text>
      </View>
    </View>
  );
}

// Mini progress bar for section groups
function GroupProgressBar({
  completed,
  total,
}: {
  completed: number;
  total: number;
}) {
  const percent = total > 0 ? (completed / total) * 100 : 0;
  const isComplete = completed >= total;

  return (
    <View
      style={{
        height: 4,
        flex: 1,
        backgroundColor: "rgba(255,255,255,0.15)",
        borderRadius: 2,
        overflow: "hidden",
      }}
    >
      <View
        style={{
          height: "100%",
          width: `${percent}%`,
          backgroundColor: isComplete ? "#4ADE80" : EMBER.colors.warm,
          borderRadius: 2,
        }}
      />
    </View>
  );
}

export default function MedicalHistoryCard({ onPress }: Props) {
  const t = useTranslations();
  const capturePress = usePressCapture("MedicalHistoryCard");
  const {
    isLoading,
    progressPercent,
    completedCategories,
    totalCategories,
    groups,
  } = useCompletion();

  const isAllComplete = completedCategories >= totalCategories;

  return (
    <View style={{ marginTop: 24 }}>
      {/* Section header */}
      <View
        className="flex-row"
        style={{
          marginBottom: 10,
          paddingHorizontal: 2,
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <SectionTitle icon={ClipboardList}>
          {t("care.medical_history.title")}
        </SectionTitle>
      </View>

      <Pressable
        onPress={
          onPress
            ? () => {
                capturePress(
                  pressLabel(t("care.medical_history.title")),
                  "care.medical_history.title",
                );
                onPress();
              }
            : undefined
        }
        style={cardSurface()}
      >
        {/* Top section: progress ring + status */}
        <View
          className="flex-row"
          style={{
            alignItems: "center",
            padding: 16,
            paddingBottom: 12,
            gap: 14,
          }}
        >
          <ProgressRing percent={isLoading ? 0 : progressPercent} />
          <View style={{ flex: 1 }}>
            <Text
              className="font-dm-sans"
              style={{
                fontSize: 15,
                fontWeight: "600",
                color: EMBER.colors.text,
              }}
            >
              {isLoading
                ? t("care.medical_history.loading")
                : isAllComplete
                  ? t("care.medical_history.all_complete")
                  : t("care.medical_history.of_done", {
                      params: {
                        completed: String(completedCategories),
                        total: String(totalCategories),
                      },
                    })}
            </Text>
            <Text
              className="font-dm-sans"
              style={{
                fontSize: 12,
                color: EMBER.colors.textMid,
                marginTop: 2,
              }}
            >
              {isLoading || isAllComplete
                ? ""
                : t("care.medical_history.sections_remaining", {
                    params: {
                      remaining: String(totalCategories - completedCategories),
                    },
                  })}
            </Text>
          </View>
          <ChevronRight size={18} color={EMBER.colors.textFaint} />
        </View>

        {/* Divider */}
        <MoonDivider
          color="rgba(255,255,255,0.12)"
          style={{ marginHorizontal: 16 }}
        />

        {/* Section groups */}
        <View style={{ padding: 16, paddingTop: 12, gap: 10 }}>
          {groups.map((group) => {
            const isComplete = group.completed >= group.total;
            return (
              <View
                key={group.key}
                className="flex-row"
                style={{
                  alignItems: "center",
                  gap: 10,
                }}
              >
                {isComplete ? (
                  <Check size={14} color="#4ADE80" strokeWidth={2.5} />
                ) : (
                  <ClipboardList
                    size={14}
                    color={EMBER.colors.textLight}
                    strokeWidth={1.5}
                  />
                )}
                <Text
                  className="font-dm-sans"
                  style={{
                    fontSize: 12,
                    fontWeight: "500",
                    color: isComplete
                      ? EMBER.colors.textMid
                      : EMBER.colors.text,
                    width: 110,
                  }}
                  numberOfLines={1}
                >
                  {t(group.labelKey)}
                </Text>
                <GroupProgressBar
                  completed={group.completed}
                  total={group.total}
                />
                <Text
                  className="font-dm-sans"
                  style={{
                    fontSize: 10,
                    fontWeight: "600",
                    color: isComplete ? "#4ADE80" : EMBER.colors.textFaint,
                    width: 28,
                    textAlign: "right",
                  }}
                >
                  {group.completed}/{group.total}
                </Text>
              </View>
            );
          })}
        </View>
      </Pressable>
    </View>
  );
}
